import { useLocation } from '@/contexts/LocationContext';
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { UserProfile } from "@shared/types";
import { CoachingLog } from "@shared/schema";
import { formatDate, getInitials, parseMarkdown } from "@/lib/utils";
import { generateCoachingInsights } from "@/lib/openai";
import { apiRequest } from "@/lib/fetchWithAuth";
import { useToast } from "@/hooks/use-toast";

interface CoachingProps {
  user: UserProfile;
}

export default function Coaching({ user }: CoachingProps) {
  const { selectedLocationIds, setSelectedLocationIds } = useLocation();
  const selectedLocation = selectedLocationIds.length === 1 ? selectedLocationIds[0] : '';
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [employeeContext, setEmployeeContext] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"upcoming" | "past" | "my-coaching">("upcoming");
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [notes, setNotes] = useState("");
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get locations
  const { data: locations } = useQuery<Array<{id: string, name: string}>>({
    queryKey: [`/api/locations?organizationId=${user.organizationId}`],
  });

  // Get team members for coaching
  const { data: teamMembers, isLoading: teamLoading, isError: teamError, isSuccess: teamSuccess } = useQuery<Array<{ id: string; fullName: string }>>({
    queryKey: [`/api/users?organizationId=${encodeURIComponent(user.organizationId || '')}&locationId=${encodeURIComponent(selectedLocation)}`],
    enabled: !!user.organizationId && !!selectedLocation,
  });
  const teamContext = JSON.stringify([user.id, user.organizationId, selectedLocation]);
  const teamAvailable = !!user.organizationId && !!selectedLocation && teamSuccess && !teamError
    && Array.isArray(teamMembers) && teamMembers.every(member => member
      && typeof member.id === 'string' && member.id.trim()
      && typeof member.fullName === 'string' && member.fullName.trim());
  const displayTeamMembers = teamAvailable ? teamMembers : [];
  const selectedEmployeeAvailable = employeeContext === teamContext
    && displayTeamMembers.some(member => member.id === selectedEmployee);
  const teamStatus = !selectedLocation ? 'Select a location to load employees.'
    : teamLoading ? 'Loading employees...'
    : !teamAvailable ? 'Employees could not be loaded. Try again or contact an administrator.'
    : displayTeamMembers.length === 0 ? 'No employees are available for this location.' : null;

  // Get coaching logs - coach perspective
  const { data: coachingLogs, isLoading: logsLoading, isError: logsError } = useQuery<CoachingLog[]>({
    queryKey: [`/api/coaching-logs?coachId=${user.id}`],
  });

  // Get coaching logs - employee perspective
  const { data: myCoachingLogs, isLoading: myLogsLoading, isError: myLogsError } = useQuery<CoachingLog[]>({
    queryKey: [`/api/coaching-logs?employeeId=${user.id}`],
  });

  // Create coaching log mutation
  const createCoachingLog = useMutation({
    mutationFn: async () => {
      if (!selectedEmployeeAvailable || !notes.trim()) {
        throw new Error('Select an available employee and enter notes');
      }
      const response = await apiRequest('/api/coaching-logs', {
        method: 'POST',
        data: {
          employeeId: selectedEmployee,
          coachId: user.id,
          date: new Date(sessionDate),
          notes,
          organizationId: user.organizationId,
          locationId: selectedLocation
        }
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/coaching-logs?coachId=${user.id}`] });
      setDialogOpen(false);
      setNotes("");
      setSessionDate(new Date().toISOString().split('T')[0]);
      toast({
        title: "Success",
        description: "Coaching session recorded successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to record coaching session",
        variant: "destructive",
      });
    }
  });

  // Handle creating a new coaching log
  const handleCreateCoachingLog = () => {
    if (!selectedEmployeeAvailable || !notes.trim()) {
      toast({
        title: "Validation Error",
        description: "Please select an available employee and enter notes",
        variant: "destructive",
      });
      return;
    }

    createCoachingLog.mutate();
  };

  // Generate AI insights for coaching
  const handleGenerateInsights = async (coachingNotes: string, employeeName: string) => {
    try {
      toast({
        title: "Generating Insights",
        description: "AI is analyzing coaching data...",
      });
      
      const insights = await generateCoachingInsights(coachingNotes, employeeName);
      
      toast({
        title: "AI Coaching Insights",
        description: insights.substring(0, 200) + (insights.length > 200 ? "..." : ""),
        duration: 5000,
      });
    } catch (error) {
      console.error("Error generating coaching insights:", error);
      toast({
        title: "Error",
        description: "Failed to generate coaching insights",
        variant: "destructive",
      });
    }
  };

  // Locations are fetched from API

  const displayLocations: Array<{id: string, name: string}> = (locations || []) as Array<{id: string, name: string}>;
  const displayCoachingLogs = (Array.isArray(coachingLogs) ? coachingLogs : []) as Array<CoachingLog>;
  const displayMyCoachingLogs = (Array.isArray(myCoachingLogs) ? myCoachingLogs : []) as Array<CoachingLog>;

  // Get employee name by ID
  const getEmployeeName = (id: string) => {
    const employee = displayTeamMembers.find(member => member.id === id);
    return employee ? employee.fullName : "Unknown Employee";
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Coaching</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="Coaching sessions and performance improvement tracking"
          ></span>
        </div>
        
        <div className="flex min-w-0 flex-wrap items-center gap-4">
          <div className="relative">
            <Select
              value={selectedLocation}
              onValueChange={(location) => {
                setSelectedEmployee('');
                setSelectedLocationIds([location]);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {displayLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <span className="mdi mdi-plus mr-2"></span>
                Record Coaching Session
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Record Coaching Session</DialogTitle>
                <DialogDescription>
                  Record feedback from a coaching conversation. This form does not schedule a meeting.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <label htmlFor="employee" className="text-sm font-medium">
                    Employee
                  </label>
                  <Select
                    value={selectedEmployeeAvailable ? selectedEmployee : ''}
                    onValueChange={(employee) => {
                      setSelectedEmployee(employee);
                      setEmployeeContext(teamContext);
                    }}
                    disabled={!teamAvailable || displayTeamMembers.length === 0}
                  >
                    <SelectTrigger id="employee">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {displayTeamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {teamStatus && <p role={teamError || (!teamLoading && !!selectedLocation && !teamAvailable) ? 'alert' : 'status'}>{teamStatus}</p>}
                  {teamAvailable && displayTeamMembers.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Employees must be assigned to this location before you can record their coaching.{' '}
                      <a href="/team" className="underline">View Users and Groups</a> or ask an administrator to check location assignments.
                    </p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="date" className="text-sm font-medium">
                    Conversation Date
                  </label>
                  <Input
                    id="date"
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                  />
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="notes" className="text-sm font-medium">
                    Coaching Notes
                  </label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter detailed notes about performance, goals, action items, etc."
                    rows={8}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateCoachingLog}
                  disabled={createCoachingLog.isPending || !selectedEmployeeAvailable || !notes.trim()}
                >
                  {createCoachingLog.isPending ? "Saving..." : "Save Coaching Log"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab as any} className="mb-6">
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming Sessions</TabsTrigger>
          <TabsTrigger value="past">Past Coaching Sessions</TabsTrigger>
          <TabsTrigger value="my-coaching">My Coaching</TabsTrigger>
        </TabsList>
        
        {/* Upcoming Sessions Tab - can be implemented later */}
        <TabsContent value="upcoming">
          <Card className="p-8 text-center">
            <h3 className="text-lg font-medium mb-2">Scheduling is not available here</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">
              Use Meeting Agendas to schedule a meeting, or record coaching feedback here.
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <span className="mdi mdi-plus mr-2"></span>
              Record Coaching Session
            </Button>
          </Card>
        </TabsContent>

        {/* Past Coaching Sessions Tab */}
        <TabsContent value="past">
          <div className="space-y-6">
            {logsLoading ? (
              [...Array(2)].map((_, index) => (
                <Skeleton key={index} className="h-64 w-full" />
              ))
            ) : logsError || !Array.isArray(coachingLogs) ? (
              <p role="alert">Coaching records could not be loaded. Please try again.</p>
            ) : (
              displayCoachingLogs.length > 0 ? (
                displayCoachingLogs.map((log) => (
                  <Card key={log.id} className="overflow-hidden">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center">
                          <Avatar className="h-10 w-10 mr-3">
                            <AvatarFallback className="bg-primary text-white">
                              {getInitials(getEmployeeName(log.employeeId))}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <CardTitle className="text-lg">{getEmployeeName(log.employeeId)}</CardTitle>
                            <p className="text-xs text-neutral-500">{formatDate(log.date)}</p>
                          </div>
                        </div>
                        <Badge>Coaching Session</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="mb-4">
                        <h3 className="text-sm font-medium mb-2">Notes</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-300">{log.notes}</p>
                      </div>
                      
                      {log.aiSummary && (
                        <div className="mb-4 p-3 bg-primary/5 rounded-md border border-primary/20">
                          <h3 className="text-sm font-medium mb-2 flex items-center">
                            <span className="mdi mdi-robot-outline mr-1 text-primary"></span>
                            AI Insights
                          </h3>
                          <p className="text-sm text-neutral-600 dark:text-neutral-300">{log.aiSummary}</p>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="p-4 pt-0 flex justify-between">
                      <Button variant="outline" size="sm">
                        <span className="mdi mdi-file-document-outline mr-1"></span>
                        View Full Log
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleGenerateInsights(log.notes, getEmployeeName(log.employeeId))}
                      >
                        <span className="mdi mdi-robot mr-1"></span>
                        Generate Insights
                      </Button>
                    </CardFooter>
                </Card>
              ))
            ) : (
              <Card className="p-8 text-center">
                <h3 className="text-lg font-medium mb-2">No Coaching Records</h3>
                <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                  You haven't recorded any coaching sessions yet.
                </p>
                <Button onClick={() => setDialogOpen(true)}>
                  <span className="mdi mdi-plus mr-2"></span>
                  Record Coaching Session
                </Button>
              </Card>
            )
          )}
        </div>
      </TabsContent>

        {/* My Coaching Tab */}
        <TabsContent value="my-coaching">
          <div className="space-y-6">
            {myLogsLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : myLogsError || !Array.isArray(myCoachingLogs) ? (
              <p role="alert">Personal coaching records could not be loaded. Please try again.</p>
            ) : (
              displayMyCoachingLogs.length > 0 ? (
                displayMyCoachingLogs.map((log) => (
                  <Card key={log.id} className="overflow-hidden">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">Coaching Session</CardTitle>
                          <p className="text-xs text-neutral-500">{formatDate(log.date)}</p>
                        </div>
                        <Badge variant="outline">My Development</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="mb-4">
                        <h3 className="text-sm font-medium mb-2">Coach's Notes</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-300">{log.notes}</p>
                      </div>
                      
                      {log.aiSummary && (
                        <div className="mb-4 p-3 bg-primary/5 rounded-md border border-primary/20">
                          <h3 className="text-sm font-medium mb-2 flex items-center">
                            <span className="mdi mdi-robot-outline mr-1 text-primary"></span>
                            AI Insights
                          </h3>
                          <p className="text-sm text-neutral-600 dark:text-neutral-300">{log.aiSummary}</p>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="p-4 pt-0 flex justify-end">
                      <Button variant="outline" size="sm">
                        <span className="mdi mdi-file-document-outline mr-1"></span>
                        View Full Log
                      </Button>
                    </CardFooter>
                </Card>
              ))
            ) : (
              <Card className="p-8 text-center">
                <h3 className="text-lg font-medium mb-2">No Personal Coaching Records</h3>
                <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                  You don't have any personal coaching session records.
                </p>
              </Card>
            )
          )}
        </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
